import { Link } from "react-router-dom";
import { Sparkles, Clock, ShieldCheck, MapPin, MessageCircle, Heart, Leaf, Users, ArrowRight, Grid2x2 } from "lucide-react";
import { site } from "@/config/site";
import { seoSections, homeFaq } from "@/content/home";
import { Seo } from "@/components/Seo";
import { LocalBusinessJsonLd } from "@/components/LocalBusinessJsonLd";
import { WhatsAppButton, CallButton } from "@/components/CtaButtons";
import { SectionHeading } from "@/components/SectionHeading";
import { ServiceCard } from "@/components/ServiceCard";
import { MapSection } from "@/components/MapSection";
import { Faq } from "@/components/Faq";
import { CtaBlock } from "@/components/CtaBlock";

const trust = [
  { icon: Clock, title: "Her Gün Açık", text: site.openingHours.text },
  { icon: MessageCircle, title: "WhatsApp ile Randevu", text: "Tek mesajla hızlı dönüş" },
  { icon: ShieldCheck, title: "Hijyen & Mahremiyet", text: "Her seans öncesi özenli hazırlık" },
  { icon: MapPin, title: "Merkezi Konum", text: "Nilüfer · Konak Mahallesi" },
];

const why = [
  { icon: Sparkles, title: "Profesyonel Uygulama", text: "Her seans, ihtiyacınız dinlenerek ve tercih ettiğiniz baskı seviyesine göre planlanır." },
  { icon: ShieldCheck, title: "Hijyen Standardı", text: "Her misafir için değiştirilen tekstiller, havalandırılan ve özenle temizlenen odalar." },
  { icon: Heart, title: "Kişiye Özel Seans", text: "Hassas bölgeleriniz ve beklentileriniz seans öncesinde konuşulur; uygulama size göre şekillenir." },
  { icon: Leaf, title: "Sakin Butik Atmosfer", text: "Loş aydınlatma, dinlendirici müzik ve hoş kokularla gerçek bir mola." },
  { icon: MapPin, title: "Kolay Ulaşım", text: "Konak Mahallesi'nde, Nilüfer merkezine ve Bursa'nın ana hatlarına yakın konum." },
  { icon: Users, title: "Şeffaf İletişim", text: "Fiyat ve süre bilgisi için WhatsApp'tan yazın; sorularınıza net yanıt alın." },
];

const steps = [
  { n: "01", title: "WhatsApp'tan Yazın", text: "Uygun gün ve saati birlikte belirleriz; sorularınızı yanıtlarız." },
  { n: "02", title: "Kısa Ön Görüşme", text: "Beklentileriniz, hassas bölgeleriniz ve baskı tercihiniz konuşulur." },
  { n: "03", title: "Masaj Seansı", text: "Seçtiğiniz uygulama sakin ve hijyenik odamızda size özel uygulanır." },
  { n: "04", title: "Dinlenme", text: "Seans sonrası acele etmeden toparlanmanız için kısa bir dinlenme." },
];

const regionLinks = [
  { text: "Bursa masaj salonu seçeneklerimizi inceleyin", path: "/bursa-masaj-salonu" },
  { text: "Nilüfer masaj salonu hizmetlerimiz", path: "/nilufer-masaj-salonu" },
  { text: "Konak Masaj Salonu", path: "/konak-masaj-salonu" },
  { text: "Klasik Masaj Bursa", path: "/bursa-klasik-masaj" },
  { text: "Medikal Masaj Bursa", path: "/bursa-medikal-masaj" },
  { text: "Soft Masaj Bursa", path: "/bursa-soft-masaj" },
  { text: "Bursa spa deneyimi", path: "/bursa-spa" },
  { text: "Nilüfer spa", path: "/nilufer-spa" },
  { text: "Bursa masaj", path: "/bursa-masaj" },
  { text: "Nilüfer masaj", path: "/nilufer-masaj" },
  { text: "Konak masaj", path: "/konak-masaj" },
];

const gallery = [
  { src: site.heroImage, alt: "Lavenza masaj odası genel görünüm", tag: "Mekan" },
  { src: "/images/bursa-klasik-masaj.webp", alt: "Masaj yağı ve havlular", tag: "Detay" },
  { src: "/images/bursa-medikal-masaj.webp", alt: "Uygulama odası ve masaj masası", tag: "Terapi" },
  { src: "/images/nilufer-soft-masaj.webp", alt: "Mumlar ve taşlarla spa atmosferi", tag: "Atmosfer" },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Bursa Masaj Salonu | Nilüfer & Konak | Lavenza"
        description="Bursa Nilüfer Konak'ta profesyonel masaj salonu. Klasik, medikal, soft masaj ve kese köpük hizmetleri için Lavenza'dan WhatsApp ile randevu alın."
        path="/"
      />
      <LocalBusinessJsonLd />

      <section className="relative isolate overflow-hidden bg-night text-white" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={site.heroImage} alt={site.heroImageAlt} width="1200" height="800" fetchPriority="high" decoding="sync" className="h-full w-full object-cover animate-ken-burns" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/30" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" aria-hidden="true" />
          <div className="absolute inset-0 bg-emerald/15 mix-blend-multiply" aria-hidden="true" />
        </div>
        <div className="relative container-wide py-20 md:py-32 lg:py-40">
          <div className="max-w-3xl reveal">
            <img
              src="/images/lavenza-spa-logo-header.png"
              alt="Lavenza Spa logo"
              width="822"
              height="620"
              loading="eager"
              decoding="async"
              className="logo-glow w-[150px] md:w-[180px] h-auto mb-6 mx-auto sm:mx-0"
              data-testid="hero-logo"
            />
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/15 px-4 py-1.5 text-xs md:text-sm text-gold backdrop-blur mb-6">
              <Sparkles size={14} aria-hidden="true" /> Nilüfer · Konak Mahallesi · Bursa
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight" data-testid="home-h1">
              <span className="gold-text">Bursa Masaj Salonu</span> <span className="block text-white">– Lavenza Nilüfer</span>
            </h1>
            <p className="mt-6 text-base md:text-xl text-white/90 leading-relaxed max-w-2xl">
              Konak Mahallesi'ndeki sakin salonumuzda klasik, medikal ve soft masaj ile kese & köpük hizmeti. Hijyenik, konforlu ve gerçekten dinlendirici; randevunuz WhatsApp'tan tek mesajla.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <CallButton testId="hero-call" />
              <WhatsAppButton testId="hero-whatsapp" />
              <Link to="/iletisim" className="btn btn-ghost-light" data-testid="hero-appointment-link">Randevu Al</Link>
            </div>
            <ul className="mt-10 grid grid-cols-3 max-w-xl gap-3 md:gap-5">
              <li className="glass"><Clock className="w-4 h-4 md:w-5 md:h-5 mx-auto text-gold mb-1" aria-hidden="true" /><div className="text-xs md:text-sm font-semibold">Her Gün</div><div className="text-[10px] md:text-xs text-muted">10:00 – 04:00</div></li>
              <li className="glass"><ShieldCheck className="w-4 h-4 md:w-5 md:h-5 mx-auto text-gold mb-1" aria-hidden="true" /><div className="text-xs md:text-sm font-semibold">Hijyenik</div><div className="text-[10px] md:text-xs text-muted">Temiz Ortam</div></li>
              <li className="glass"><MessageCircle className="w-4 h-4 md:w-5 md:h-5 mx-auto text-gold mb-1" aria-hidden="true" /><div className="text-xs md:text-sm font-semibold">WhatsApp</div><div className="text-[10px] md:text-xs text-muted">Hızlı Randevu</div></li>
            </ul>
          </div>
        </div>
      </section>

      <section className="relative py-16 md:py-20 bg-gradient-to-br from-emerald-dark via-emerald to-emerald-dark text-white overflow-hidden" aria-labelledby="guven-title" data-testid="trust-section">
        <div className="absolute inset-0 pattern-dots opacity-[0.05]" aria-hidden="true" />
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
        <div className="relative container-wide">
          <SectionHeading eyebrow="Lavenza Güvencesi" accent="Nilüfer'de" title="Sakin ve Güvenilir Bir Masaj Adresi" lead="Abartılı vaatler yerine, her seansta aynı özen." light id="guven-title" />
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {trust.map(({ icon: Icon, title, text }) => (
              <li key={title} className="rounded-2xl border border-gold/20 bg-white/[0.04] backdrop-blur-sm p-5 md:p-7 text-center hover:border-gold/60 hover:bg-white/[0.08] transition-colors">
                <div className="inline-flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-xl bg-gold/15 border border-gold/30 mb-3"><Icon className="w-5 h-5 md:w-6 md:h-6 text-gold" aria-hidden="true" /></div>
                <h3 className="text-sm md:text-base font-bold">{title}</h3>
                <p className="mt-1 text-xs md:text-sm text-white/70">{text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="neden-lavenza" className="section bg-white" aria-labelledby="neden-title" data-testid="why-section">
        <div className="container-wide">
          <SectionHeading eyebrow="Neden Biz" accent="Lavenza" title="Farkı" lead="Bursa Nilüfer'de profesyonel masaj deneyimini şekillendiren 6 temel ilke" id="neden-title" />
          <ul className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {why.map(({ icon: Icon, title, text }) => (
              <li key={title} className="group relative card p-6 md:p-7 hover-lift overflow-hidden">
                <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-gold/5 group-hover:bg-gold/15 transition-colors" aria-hidden="true" />
                <div className="relative">
                  <div className="icon-box mb-5"><Icon className="w-7 h-7" aria-hidden="true" /></div>
                  <h3 className="font-bold text-lg md:text-xl mb-2">{title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="hizmetler" className="section bg-gradient-to-b from-white to-offwhite" aria-labelledby="hizmetler-title" data-testid="services-section">
        <div className="container-wide">
          <SectionHeading eyebrow="Hizmetlerimiz" accent="Profesyonel Masaj" title="Hizmetleri" lead={`Klasik, medikal, soft masaj ve kese & köpük. ${site.priceNote}`} id="hizmetler-title" />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {site.services.map((s, i) => <ServiceCard key={s.slug} service={s} index={i} />)}
            <Link to="/masaj-hizmetleri" className="group card p-6 flex flex-col justify-center items-center text-center emerald-gradient text-white hover-lift border-0" data-testid="all-services-card">
              <Grid2x2 className="w-10 h-10 text-gold mb-4" aria-hidden="true" />
              <h3 className="text-xl md:text-2xl font-bold">Tüm Hizmetler</h3>
              <p className="mt-2 text-sm text-white/75">Tüm masaj türleri ve detaylar</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-gold group-hover:gap-2 transition-all">İncele <ArrowRight size={16} aria-hidden="true" /></span>
            </Link>
          </div>
        </div>
      </section>

      <section id="hakkimizda" className="section bg-white" aria-labelledby="hakkimizda-title" data-testid="about-section">
        <div className="container-wide">
          <SectionHeading eyebrow="Hakkımızda" accent="Bursa Nilüfer'de" title="Sakin Bir Mola" id="hakkimizda-title" />
          <div className="max-w-3xl mx-auto text-base md:text-lg text-muted leading-relaxed space-y-5">
            <p>Lavenza Masaj Salonu, Bursa Nilüfer'in Konak Mahallesi'nde yer alan, sadeliği ve özeni merkeze alan butik bir masaj salonudur. Klasik, medikal ve soft masaj ile kese & köpük hizmetlerini hijyenik ve sakin bir ortamda sunuyoruz.</p>
            <p>Her seans öncesinde misafirimizin ihtiyacı dinlenir ve uygulama ona göre planlanır. Gösterişli vaatler yerine temiz, konforlu ve güvenli bir ortamda gerçekten dinlendiğinizi hissetmenizi amaçlıyoruz. Sorularınız için WhatsApp'tan yazmanız yeterli.</p>
          </div>
          <ul className="mt-10 flex flex-wrap justify-center gap-3" data-testid="region-links">
            {regionLinks.map((l) => <li key={l.path}><Link to={l.path} className="chip">{l.text}</Link></li>)}
          </ul>
          <div className="mt-8 text-center">
            <Link to="/hakkimizda" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald hover:gap-2 transition-all" data-testid="about-page-link">Hakkımızda sayfası <ArrowRight size={16} aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      <section id="surec" className="section bg-offwhite" aria-labelledby="surec-title" data-testid="process-section">
        <div className="container-wide">
          <SectionHeading eyebrow="Süreç" accent="Seans Akışı" title="4 Adımda" lead="Randevudan dinlenmeye kadar sade ve net bir süreç" id="surec-title" />
          <ol className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s) => (
              <li key={s.n} className="card p-6 hover-lift">
                <span className="text-4xl font-extrabold gold-text">{s.n}</span>
                <h3 className="mt-3 font-bold text-lg">{s.title}</h3>
                <p className="mt-2 text-sm text-muted leading-relaxed">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="bursa-nilufer-masaj" className="section bg-white" aria-labelledby="seo-title" data-testid="seo-content-section">
        <article className="container-wide max-w-3xl">
          <div className="text-center mb-10">
            <span className="eyebrow mb-3">Lavenza Deneyimi</span>
            <h2 id="seo-title" className="section-title">Bursa Nilüfer'de Profesyonel Masaj Deneyimi</h2>
            <div className="section-divider my-5" />
          </div>
          <div className="prose-seo">
            {seoSections.map((s, i) => (
              <div key={i}>
                {s.heading && <h3>{s.heading}</h3>}
                {s.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="relative py-16 md:py-20 bg-gradient-to-br from-emerald-dark via-emerald to-emerald-dark text-white overflow-hidden" data-testid="quote-cta">
        <div className="absolute inset-0 pattern-dots opacity-[0.05]" aria-hidden="true" />
        <div className="relative container-wide text-center max-w-3xl">
          <p className="text-xl md:text-2xl font-medium leading-relaxed">“Kendinize iyi bakmak için doğru zamanı beklemeyin. Sorularınız ve randevunuz için bize WhatsApp'tan yazmanız yeterli.”</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <WhatsAppButton label="WhatsApp'tan Randevu Al" testId="quote-whatsapp" />
            <CallButton label="Hemen Ara" testId="quote-call" />
          </div>
          <Link to="/masaj-hizmetleri" className="mt-6 inline-flex items-center gap-1 text-sm text-gold hover:gap-2 transition-all">Tüm hizmet detaylarını inceleyin <ArrowRight size={16} aria-hidden="true" /></Link>
        </div>
      </section>

      <section id="mekan" className="section bg-white" aria-labelledby="mekan-title" data-testid="gallery-section">
        <div className="container-wide">
          <SectionHeading eyebrow="Galeri · Mekanımız" accent="Atmosferi" title="Keşfedin" lead="Sakin, hijyenik ve konforlu salonumuzdan kareler" id="mekan-title" />
          <ul className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {gallery.map((g) => (
              <li key={g.src + g.tag} className="group relative overflow-hidden rounded-2xl">
                <img src={g.src} alt={g.alt} width="1200" height="800" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" aria-hidden="true" />
                <span className="absolute bottom-3 left-3 rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold text-night uppercase tracking-wider">{g.tag}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Faq items={homeFaq} />
      <MapSection />
      <CtaBlock />
    </>
  );
}
